// Bundles a defo page's outputs into a `{ payload, images }` object
// ready for POST /api/inspections/{id}/defo-results.
//
// Profile PNGs are captured at 800 × 2000 px — sized to print as an
// ~8 cm × 20 cm portrait image on the inspection report.

import { svgToPng, stripDataUrlPrefix } from './svgToPng';

// Standard PNG dimensions for scanline-profile exports (2× for crisp print).
//   • Shell scanlines are portrait   → 2028 × 4956 px  ((1014 × 2478) × 2)
//   • Floor scanline pairs landscape  → 1864 × 1054 px  ((932 × 527) × 2)
// Each exported image carries a title band at the top (see composeTitledImage).
export const PROFILE_PNG_W = 2028;
export const PROFILE_PNG_H = 4956;
export const PROFILE_PNG_LANDSCAPE_W = 1864;
export const PROFILE_PNG_LANDSCAPE_H = 1054;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = src;
  });
}

/** Compose a title band + the plot onto a w×h white PNG. */
async function composeTitledImage(plotDataUrl, w, h, title) {
  const img = await loadImage(plotDataUrl);
  const titleH = Math.round(h * 0.055);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = '#111827';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `bold ${Math.round(titleH * 0.5)}px Arial, sans-serif`;
  ctx.fillText(title, w / 2, titleH / 2);
  ctx.drawImage(img, 0, titleH, w, h - titleH);
  return canvas.toDataURL('image/png');
}

/** Capture every chart SVG inside `chartGridEl` to a titled PNG.
 *  Returns { <prefix>_1: base64, <prefix>_2: base64, ... } in DOM order.
 *  Empty / no-data charts are skipped. `titleFor(nth, total)` builds the
 *  title drawn above each plot.
 */
async function captureScanlineSvgs(
  chartGridEl,
  prefix = 'scanline',
  w = PROFILE_PNG_W,
  h = PROFILE_PNG_H,
  titleFor = (n) => `Scanline ${n}`,
) {
  if (!chartGridEl) return {};
  const charts = chartGridEl.querySelectorAll('.defo-chart');
  const total = charts.length;
  const out = {};
  let pos = 0;
  for (const chart of charts) {
    pos += 1;
    // Name/title by the chart's own scanline number when it carries one
    // (shell page: muted scanlines aren't rendered, so DOM position would
    // shift later scanlines down a number); else by DOM position.
    const nth = parseInt(chart.dataset.scanline, 10) || pos;
    const svg = chart.querySelector('svg');
    if (!svg) continue; // empty-bin chart, no svg rendered
    const titleH = Math.round(h * 0.055);
    const plotUrl = await svgToPng(svg, w, h - titleH);   // plot sized to sit below the title
    const composed = await composeTitledImage(plotUrl, w, h, titleFor(nth, total));
    out[`${prefix}_${nth}`] = stripDataUrlPrefix(composed);
  }
  return out;
}

/** Convert an HTMLCanvasElement to a base64 PNG (no data URL prefix). */
function captureCanvas(canvas) {
  if (!canvas) return null;
  return stripDataUrlPrefix(canvas.toDataURL('image/png'));
}

/** Shell page bundle.
 *
 *  Inputs are pulled directly from the page's state where possible so
 *  the page just hands over its current snapshot at save time. DOM
 *  refs (chartGridEl, c2mCanvasEl) are queried on the page side. */
export async function bundleShellResults({
  result, angles, oovRows, mutedScanlines = [],
  chartGridEl, c2mCanvasEl,
}) {
  if (!result) throw new Error('No deformation result to save');

  const payload = {
    pointCount: result.pointCount,
    lineCount: result.lineCount,
    durationMs: result.durationMs,
    binsPerCircle: result.binsPerCircle,
    thetaBinDeg: result.thetaBinDeg,
    cellCount: result.cellCount,
    bounds: result.bounds,      // m — frontend converts to mm at render
    center: result.center,
    nominal: result.nominal,    // mm — already in display units
    angles,
    // 1-based numbers of scanlines the user switched off ("not computed"):
    // no OOV values, no profile image, no heatmap arrow.
    mutedScanlines,
    oov: (oovRows || []).map((r) => ({
      scanline: r.scanline,
      angle: r.angle,
      snapped: r.snapped,
      rBottom: r.rBottom,
      rTop: r.rTop,
      oov: r.oov,
      allow: r.allow,
      pct: r.pct,
      ...(r.muted ? { muted: true } : {}),
    })),
  };

  const images = {};
  Object.assign(images, await captureScanlineSvgs(
    chartGridEl, 'scanline', PROFILE_PNG_W, PROFILE_PNG_H,
    (n) => `Shell Scanline ${n}`,
  ));
  const c2m = captureCanvas(c2mCanvasEl);
  if (c2m) images.c2m_unrolled = c2m;

  return { payload, images };
}

/** Floor page bundle. Floor has more drawables — XY scatter, floor C2M,
 *  paired-scanline profiles, settlement tables. */
export async function bundleFloorResults({
  result, angles, settlementData, shape,
  scanlineGridEl, c2mCanvasEl,
}) {
  if (!result) throw new Error('No deformation result to save');

  const payload = {
    pointCount: result.pointCount,
    lineCount: result.lineCount,
    durationMs: result.durationMs,
    bounds: result.bounds,
    center: result.center,
    nominal: result.nominal,
    angles,
    shape: shape ? {
      profile: shape.profile,
      coefficients: shape.coefficients,
      tilt: shape.tilt,
    } : null,
    // Field names must match the page's settlementData (localDiff / general /
    // edgeSettlement) — the old localSettlement/generalSettlement keys were
    // undefined, so local + maximum settlements never made it to the main page.
    settlement: settlementData ? {
      local:   settlementData.localDiff      || [],
      general: settlementData.general        || null,
      edge:    settlementData.edgeSettlement || [],
    } : null,
  };

  const images = {};
  Object.assign(images, await captureScanlineSvgs(
    scanlineGridEl, 'pair',
    PROFILE_PNG_LANDSCAPE_W, PROFILE_PNG_LANDSCAPE_H,
    (n, total) => `Floor Scanline ${n} - ${n + total}`,
  ));
  const c2m = captureCanvas(c2mCanvasEl);
  if (c2m) images.c2m_floor = c2m;

  return { payload, images };
}
